import React, { useEffect, Suspense, useMemo, useState } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import { Button, Box, Text } from "@mantine/core";

import PreviewViewport from "../components/PreviewViewport";
import ThirdPersonPreviewViewport from "../components/ThirdPersonPreviewViewport";
import { useObjectStore } from "../zustand/objects";
import { getSpecificUserProjectGuestAccessAPI, postRealtimeToken } from "../api";
import SwitchVRMode from "../components/SwitchVRMode";
import ChangePreviewArtboard from "../components/ChangePreviewArtboard";
import PreviewChrome from "../components/PreviewChrome";
import {
  debouncedRealtimeEventUpdate,
  cancelEditorRealtimeDebouncers,
  isPresenceUpdateType,
} from "../utils/realtimeDebounce";
import {
  editorModalBorder,
  getPreviewChromeIconColour,
  MBX_DARK_BACKGROUND_FILL,
  previewCanvasInsetStyle,
} from "../styleFile";

function useQuery() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}

const SharePreviewPage = () => {
  const [realtime, setRealtime] = useState(false);

  let query = useQuery();

  const queryString = query.get("artboard");

  const {
    currentObjectArtboard,
    presence,
    xanoClient,
    updateArtboard,
    artboards,
    unloadObjects,
    getSpecificUserProject,
    getSpecificUserProjectGuest,
    loadObjects,
    previewVR,
    objectsAreLoaded,
    floorColour,
    viewportThemeOverride,
  } = useObjectStore();
  const isDarkTopBar = viewportThemeOverride === "dark";
  const backgroundFill = isDarkTopBar ? MBX_DARK_BACKGROUND_FILL : "#FFFFFF";
  const previewToolbarIconColour = getPreviewChromeIconColour(isDarkTopBar);

  const lastProjectSaveAt = useObjectStore((s) => s.lastProjectSaveAt);
  const lastProjectRemoteUpdateAt = useObjectStore((s) => s.lastProjectRemoteUpdateAt);

  const { projectKey } = useParams();

  const [guest, setGuest] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      await Promise.resolve(unloadObjects());
      if (cancelled) return;

      const access = await Promise.resolve(
        getSpecificUserProjectGuestAccessAPI(projectKey)
      );
      if (cancelled) return;

      if (access === true) {
        await Promise.resolve(getSpecificUserProjectGuest(projectKey));
        if (cancelled) return;
        setGuest(true);
      } else {
        setGuest(false);
        await Promise.resolve(getSpecificUserProject(projectKey));
        if (cancelled) return;
      }

      await Promise.resolve(
        queryString ? updateArtboard(queryString) : updateArtboard("1")
      );
      if (cancelled) return;

      await Promise.resolve(loadObjects());
    })();

    return () => {
      cancelled = true;
    };
  }, [
    getSpecificUserProject,
    getSpecificUserProjectGuest,
    loadObjects,
    projectKey,
    queryString,
    unloadObjects,
    updateArtboard,
  ]);

  useEffect(() => {
    if (!projectKey) return undefined;

    let projectChannel;
    try {
      projectChannel = xanoClient.channel(`project/${projectKey}`, { presence: true });
      if (!projectChannel || typeof projectChannel.on !== "function") {
        return undefined;
      }

      projectChannel.on("connection_status", (action) => {
        if (action.payload.status === "connected") {
          setRealtime(true);
          xanoClient.setRealtimeAuthToken(localStorage.getItem("user"));
          postRealtimeToken().then((res) => {
            useObjectStore.getState().setRealtimeToken(res.id);
          });
        } else if (action.payload.status === "disconnected") {
          setRealtime(false);
        }
      });

      projectChannel.on("presence_update", (action) => {
        const st = useObjectStore.getState();
        if (action.payload.action === "join") {
          st.addOrUpdatePresence(action.payload.presence.socketId, "editor", action);
        } else if (action.payload.action === "leave") {
          st.removePresence(action.payload.presence.socketId);
        }
      });

      projectChannel.on("message", (action) => {
        const st = useObjectStore.getState();
        if (
          action.options?.type != null &&
          action.options.type !== "updateFunction"
        ) {
          return;
        }

        const p = action.payload;

        if (isPresenceUpdateType(p.updateType)) {
          if (p.function === "bringToMe") {
            const ab = p.artboard;
            if (ab != null && ab !== "") {
              st.updateArtboardAndPreviewCamera(String(ab));
            }
            return;
          }

          const field = p.field;
          const chunk = field && p.update?.[field];
          const socketId = action.client?.socketId;
          if (field != null && chunk != null && socketId != null) {
            st.recordRealtimeClientSocketFromPresenceAction(action);
            st.addOrUpdatePresence(socketId, chunk.page, action);
          }
          return;
        }

        // Do not gate on realtime_token (per-user). Same account may have editor + share preview open.
        debouncedRealtimeEventUpdate(action);
      });
    } catch (err) {
      console.log("Error setting up Xano channel:", err);
    }

    return () => {
      cancelEditorRealtimeDebouncers();
      useObjectStore.getState().setRealtimeClientSocketId(null);
      if (projectChannel && typeof projectChannel.destroy === "function") {
        projectChannel.destroy();
      }
    };
  }, [projectKey, xanoClient]);

  useEffect(() => {
    if (!projectKey) return undefined;
    const id = window.setInterval(() => {
      useObjectStore.getState().pruneStalePresence(28000);
    }, 4000);
    return () => window.clearInterval(id);
  }, [projectKey]);

  const shareFallback = (
    <Box
      style={{
        ...previewCanvasInsetStyle,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--mbx-background-fill)",
        zIndex: 1,
      }}
    >
      <Text size="sm" c="dimmed">
        Loading preview…
      </Text>
    </Box>
  );

  return (
    <>
      <Box
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: backgroundFill,
          zIndex: 0,
        }}
      />
      <PreviewChrome
        isDarkTopBar={isDarkTopBar}
        connected={realtime}
        lastSaveAt={lastProjectSaveAt}
        lastRemoteUpdateAt={lastProjectRemoteUpdateAt}
        toolbarLeading={!previewVR ? <ChangePreviewArtboard /> : null}
        toolbarTrailing={
          previewVR ? (
            <>
              <Button
                variant="default"
                component={Link}
                to={`/story/${projectKey}`}
                size="sm"
                radius={8}
                styles={{ root: { borderColor: editorModalBorder } }}
              >
                View Storyboard
              </Button>
              <SwitchVRMode iconColour={previewToolbarIconColour} />
            </>
          ) : (
            <SwitchVRMode iconColour={previewToolbarIconColour} />
          )
        }
      />
      <Suspense fallback={shareFallback}>
        {previewVR ? (
          objectsAreLoaded && (
            <PreviewViewport
              presence={presence}
              guestMode={guest}
              floorColour={floorColour}
              projectKey={projectKey}
              artboard={currentObjectArtboard}
              artboards={artboards}
            />
          )
        ) : (
          objectsAreLoaded && (
            <ThirdPersonPreviewViewport
              presence={presence}
              artboards={artboards}
              guestMode={guest}
              floorColour={floorColour}
              projectKey={projectKey}
              currentObjectArtboard={currentObjectArtboard}
            />
          )
        )}
      </Suspense>
    </>
  );
};

export default SharePreviewPage;
